import type { Unsubscribe } from '@bldsoft/cappie/live'
import type { ListenEvent, ListenOptions } from '@bldsoft/cappie/rest'
import type { Event, EventCallable } from 'effector'

import { listen } from '@bldsoft/cappie/rest'
import {
  createEffect,
  createEvent,
  createStore,
  sample,
  scopeBind,
} from 'effector'

/**
 * Wraps Cappie's listen function to create a start/stop event pair and an event for the listen result
 *
 * @param node - The Cappie node to listen to
 * @param options - The options to pass to the listen function
 * @returns An object with start, stop, and event events
 */
export function createListen<N>(
  node: N,
  options?: ListenOptions
): {
  start: EventCallable<void>
  stop: EventCallable<void>
  event: Event<ListenEvent<N>>
} {
  const start = createEvent<void>()
  const stop = createEvent<void>()
  const event = createEvent<ListenEvent<N>>()

  const listenFx = createEffect(() => {
    const listener = scopeBind(event, { safe: true })
    return listen(node, listener, options)
  })

  const unlistenFx = createEffect((unlisten: Unsubscribe) => unlisten())

  const $starting = createStore(false)
    .on(listenFx, () => true)
    .on(listenFx.finally, () => false)

  const $started = createStore(false)
    .on(listenFx.doneData, () => true)
    .on(stop, () => false)

  const $unlisten = createStore<Unsubscribe | null>(null).on(
    listenFx.doneData,
    (_, unlisten) => unlisten
  )

  sample({
    clock: start,
    source: { starting: $starting, started: $started },
    filter: ({ starting, started }) => !starting && !started,
    target: listenFx,
  })

  sample({
    clock: stop,
    source: $unlisten,
    filter: (unlisten) => unlisten != null,
    target: unlistenFx,
  })

  return {
    start,
    stop,
    event,
  }
}
